import { useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { TEAM, TESTIMONIALS } from "@/data/content";

const initials = (name) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("");

export default function Studio() {
  const [openIdx, setOpenIdx] = useState(null);
  const active = openIdx !== null ? TEAM[openIdx] : null;

  return (
    <section
      id="studio"
      className="relative bg-black px-5 sm:px-8 lg:px-12 py-24 sm:py-32"
      data-testid="studio"
    >
      <div className="mx-auto max-w-7xl">
        {/* Intro */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-24">
          <div className="lg:col-span-5">
            <span className="mb-5 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-500">
              <span className="h-px w-10 bg-zinc-600" />
              The Crew
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white">
              A crew wired for the impossible
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-4">
            <p className="text-lg sm:text-xl font-light leading-relaxed text-zinc-400">
              Vision Studio is led by a tight collective of designers,
              programmers and production veterans. Tap a portrait to read the
              story behind the console.
            </p>
          </div>
        </div>

        {/* Crew grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-28">
          {TEAM.map((m, i) => (
            <motion.button
              key={m.name}
              type="button"
              data-testid={`team-${i}`}
              data-cursor="hover"
              onClick={() => setOpenIdx(i)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group text-left rounded-2xl border border-white/10 bg-[#050505] p-4 flex flex-col gap-5 transition-colors duration-500 hover:border-white/25 hover:bg-[#0c0c0e]"
            >
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-xl bg-[#0a0a0a]">
                {m.photo ? (
                  <img
                    src={m.photo}
                    alt={m.name}
                    className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <span className="font-display text-7xl font-extrabold tracking-tighter text-white/10 group-hover:text-white/20 transition-colors">
                      {initials(m.name)}
                    </span>
                  </div>
                )}
                <div
                  className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 0%, rgba(255,255,255,0.14), transparent 60%)",
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-5">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 group-hover:text-white transition-colors">
                    Read Bio →
                  </span>
                </div>
              </div>
              <div className="px-2 pb-2">
                <h3 className="font-display text-xl font-semibold text-white">
                  {m.name}
                </h3>
                <p className="mt-1 text-xs uppercase tracking-[0.15em] text-zinc-500">
                  {m.role}
                </p>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.blockquote
              key={i}
              data-testid={`testimonial-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className="rounded-2xl border border-white/10 bg-[#050505] p-9 sm:p-11"
            >
              <span className="font-display text-5xl leading-none text-zinc-700">
                “
              </span>
              <p className="mt-2 text-lg sm:text-xl font-light leading-relaxed text-zinc-200">
                {t.quote}
              </p>
              <footer className="mt-8 flex items-center gap-4">
                <span className="h-px w-8 bg-zinc-600" />
                <div>
                  <div className="text-sm font-semibold text-white">{t.name}</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-zinc-500">
                    {t.org}
                  </div>
                </div>
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </div>

      <Dialog
        open={openIdx !== null}
        onOpenChange={(v) => {
          if (!v) setOpenIdx(null);
        }}
      >
        <DialogContent
          data-testid="crew-dialog"
          className="max-w-2xl border-white/10 bg-[#050505] text-zinc-200"
        >
          {active && (
            <div>
              <DialogTitle className="sr-only">{active.name}</DialogTitle>
              <div className="flex items-start gap-5">
                <div className="hidden shrink-0 sm:block h-24 w-24 overflow-hidden rounded-xl border border-white/10 bg-[#0a0a0a]">
                  {active.photo ? (
                    <img
                      src={active.photo}
                      alt={active.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <span className="font-display text-3xl font-extrabold text-white/25">
                        {initials(active.name)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="pr-6">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                    Crew
                  </span>
                  <h3 className="mt-1 font-display text-3xl sm:text-4xl font-bold uppercase leading-none tracking-tight text-chrome">
                    {active.name}
                  </h3>
                  <p className="mt-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                    {active.role}
                  </p>
                </div>
              </div>
              <p className="mt-6 text-sm sm:text-base font-light leading-relaxed text-zinc-300">
                {active.bio}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
